import { Link } from "@tanstack/react-router";
import { ChevronDownIcon } from "lucide-react";
import { type ReactNode, useLayoutEffect, useRef, useState } from "react";
import { useT } from "@/i18n";
import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import { SubTabs } from "./layout";
import type {
  NavigationGroup,
  NavigationLink,
  NavigationLinkSection,
  NavigationView,
} from "./navigation-dialog";
import { navigationDialog, useWarmNavigationDialog } from "./navigation-dialog-loader";

/**
 * Whether the phone's tab row, at its natural width, fits the nav.
 *
 * Measured, not guessed from a character count: Ukrainian labels run long,
 * a coarse pointer grows the triggers, and the web font lands after first
 * paint. Both boxes are observed, so rotation and the font re-decide; the
 * row's words are a dependency, so a locale switch or a new view re-measures
 * in the same layout pass instead of painting a stale answer for a frame. The
 * first measurement is a layout effect too, so a phone never paints the wrong
 * control first. Until there is a width to compare with (no `ResizeObserver`,
 * or jsdom, where every box is 0 wide) the answer is no: the dialog, which
 * works at any width.
 */
function useTabsFit(words: string | undefined) {
  const [fits, setFits] = useState(false);
  const room = useRef<HTMLElement>(null);
  const row = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const nav = room.current;
    const natural = row.current;
    if (words === undefined || nav === null || natural === null) return;
    if (typeof ResizeObserver === "undefined") return;
    // The copy is out of flow, so what this sets can never widen the nav it
    // is compared with; swapping button for tabs only changes the height.
    const measure = (): void => {
      const width = nav.getBoundingClientRect().width;
      setFits(width > 0 && natural.getBoundingClientRect().width <= width);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(nav);
    observer.observe(natural);
    return () => observer.disconnect();
  }, [words]);
  return { fits: words !== undefined && fits, room, row };
}

/** Local sections. On phones: the sections themselves as tabs when there is
 * one plain list and it fits, otherwise a named current-view control that
 * opens the sheet. The same ordered tabs on desktop. The sheet stays out of the
 * main chunk: a phone fetches it when the page goes idle, a laptop never. */
export function SectionNav<T extends string>({
  title,
  views = [],
  groups,
  active,
  onSelect,
  links = [],
  trailing,
  menuSections = [],
}: {
  title: string;
  views?: NavigationView<T>[];
  groups?: NavigationGroup<T>[];
  active: T;
  onSelect: (id: T) => void;
  links?: NavigationLink[];
  /**
   * The viewer's own shortcuts at the right end of the DESKTOP tab row (the
   * board's team tiles, 2026-09-15), outside the nav landmark. A phone does
   * not show them on the page: they are `menuSections` in the sheet.
   */
  trailing?: ReactNode;
  /**
   * Destinations of the viewer's own in the phone's section sheet, each under
   * its heading (the board's «Ваші команди», owner 2026-09-15: rows under the
   * selector pushed the content down). Present, the phone always shows the
   * named selector, never inline tabs, so the sheet stays reachable.
   */
  menuSections?: NavigationLinkSection[];
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  // Loaded ahead on a phone and rendered without Suspense, whose reveal
  // throttle cost the first tap 300 ms (`navigation-dialog-loader.ts`).
  const NavigationDialog = navigationDialog.useComponent();
  useWarmNavigationDialog();
  const sections = (groups ?? [{ id: "sections", label: title, views }]).filter(
    (group) => group.views.length > 0,
  );
  const all = sections.flatMap((group) => group.views);
  const current = all.find((view) => view.id === active);
  // Only one plain list may go inline: the organiser's named groups and the
  // board's page links are what the sheet's headings are for (GLASS.md §5).
  const inline =
    menuSections.length === 0 && sections.length === 1 && links.length === 0
      ? sections[0]?.views
      : undefined;
  const { fits, room, row } = useTabsFit(
    inline?.map((view) => `${view.label} ${view.count ?? ""}`).join("\n"),
  );
  if (all.length === 0 && links.length === 0) {
    return trailing === undefined ? null : <div className="hidden adm:flex">{trailing}</div>;
  }
  const nav = (
    <nav
      ref={room}
      className={cn(
        "relative",
        trailing !== undefined && "min-w-0 flex-auto basis-full adm:basis-auto",
      )}
      aria-label={t("nav.sections", { name: title })}
    >
      {inline !== undefined && fits ? (
        // A segmented control: the row spans the nav and the tabs share it.
        <SubTabs
          className="mb-0 adm:hidden"
          listClassName="flex-nowrap *:flex-1"
          views={inline}
          active={active}
          onSelect={onSelect}
        />
      ) : (
        <Button
          variant="outline"
          className="w-full justify-between adm:hidden"
          aria-label={t("nav.chooseSection", { name: current?.label ?? title })}
          aria-expanded={open}
          aria-haspopup="dialog"
          onPointerDown={() => void navigationDialog.load()}
          onClick={() => {
            if (NavigationDialog !== null) setOpen(true);
            else void navigationDialog.load().then((ok) => ok && setOpen(true));
          }}
        >
          <span className="truncate">{current?.label ?? title}</span>
          <ChevronDownIcon aria-hidden="true" />
        </Button>
      )}
      {inline !== undefined && (
        // The same row at its natural width, for the measurement only:
        // never seen, focused or announced. The zero-height clip keeps a
        // row wider than the phone from scrolling the page sideways.
        <div
          aria-hidden="true"
          inert
          className="pointer-events-none invisible absolute inset-x-0 top-0 h-0 overflow-hidden adm:hidden"
        >
          <div ref={row} className="w-max">
            <SubTabs
              className="mb-0"
              listClassName="w-max flex-nowrap"
              views={inline}
              active={active}
              onSelect={() => undefined}
            />
          </div>
        </div>
      )}
      <div className="no-bar hidden flex-nowrap items-center gap-2 overflow-x-auto adm:flex">
        {sections.map((group) => (
          <SubTabs
            key={group.id}
            className="mb-0 shrink-0"
            listClassName="w-auto"
            views={group.views.map(({ id, label }) => ({ id, label }))}
            active={active}
            onSelect={onSelect}
          />
        ))}
        {links.map((link) => (
          <Button
            key={link.href}
            variant="ghost"
            className="shrink-0 text-muted-foreground"
            render={<Link to={link.href} />}
          >
            {link.label}
          </Button>
        ))}
      </div>
    </nav>
  );
  return (
    <>
      {trailing === undefined ? (
        nav
      ) : (
        <div className="flex flex-wrap items-center gap-2 adm:gap-x-4">
          {nav}
          {/* Desktop only: a phone finds these in the sheet. `contents`, so
              the shortcut itself is the row's flex item. */}
          <div className="hidden adm:contents">{trailing}</div>
        </div>
      )}
      {open && NavigationDialog !== null && (
        <NavigationDialog
          title={title}
          groups={sections}
          linkSections={menuSections}
          pageLinks={links}
          active={active}
          onSelect={(id) => {
            const next = all.find((view) => view.id === id);
            if (next) onSelect(next.id);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
